import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Download, Printer, Calendar } from 'lucide-react';

const TimetableGrid = ({ 
  entries = [], 
  viewMode = 'weekly', 
  onViewModeChange,
  onExport,
  onPrint,
  userRole = 'student'
}) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Generate time slots from 8:00 AM to 5:00 PM (40-minute periods)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 17; hour++) {
      for (let minute = 0; minute < 60; minute += 40) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };
  
  const timeSlots = generateTimeSlots();

  // Helper to convert TIME format (HH:MM:SS) to HH:MM
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // Extract HH:MM from HH:MM:SS
  };

  // Helper to check if a time slot contains an entry
  const timeSlotContainsEntry = (slotTime, entryStartTime, entryEndTime) => {
    const slot = slotTime;
    const start = formatTime(entryStartTime);
    const end = formatTime(entryEndTime);
    
    // Check if slot time is within entry time range
    return slot >= start && slot < end;
  };

  // Group entries by day and time slot
  const groupedEntries = React.useMemo(() => {
    const grouped = {};
    days.forEach(day => {
      grouped[day] = {};
      timeSlots.forEach(time => {
        grouped[day][time] = [];
      });
    });

    entries.forEach(entry => {
      const day = entry.dayOfWeek;
      if (grouped[day]) {
        // Find all time slots that this entry spans
        timeSlots.forEach(slot => {
          if (timeSlotContainsEntry(slot, entry.startTime, entry.endTime)) {
            grouped[day][slot].push(entry);
          }
        });
      }
    });

    return grouped;
  }, [entries, days, timeSlots]);

  // Color mapping for subjects
  const getSubjectColor = (subjectName) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-red-100 text-red-800 border-red-200',
    ];
    
    const hash = subjectName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  const TimetableCard = ({ entry, className = '' }) => {
    const subjectName = entry.subjectName || entry.subject?.name || 'Unknown';
    const teacherName = entry.teacherName || (entry.teacher ? `${entry.teacher.firstName || ''} ${entry.teacher.lastName || ''}`.trim() : '');
    const className_ = entry.className || entry.class?.name || '';
    const room = entry.room || '';
    const startTime = formatTime(entry.startTime || entry.time?.split(' - ')[0] || '');
    const endTime = formatTime(entry.endTime || entry.time?.split(' - ')[1] || '');
    
    // Build tooltip content
    const tooltipContent = (
      <div className="space-y-1 text-sm">
        <div className="font-semibold">{subjectName}</div>
        {userRole === 'student' && className_ && <div>Class: {className_}</div>}
        {teacherName && <div>Teacher: {teacherName}</div>}
        {room && <div>Room: {room}</div>}
        {(startTime || endTime) && <div>Time: {startTime} - {endTime}</div>}
      </div>
    );
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`${className} ${getSubjectColor(subjectName)} rounded p-1.5 border-2 h-full flex flex-col justify-center cursor-pointer hover:shadow-md transition-shadow min-h-[60px] overflow-hidden`}
          >
            <div className="space-y-0.5">
              <div className="flex items-center justify-between gap-1">
                <Badge className={`text-[10px] font-semibold px-1 py-0 ${getSubjectColor(subjectName)} truncate max-w-[60%]`}>
                  {subjectName}
                </Badge>
                {room && (
                  <span className="text-[9px] text-gray-600 font-medium truncate">{room}</span>
                )}
              </div>
              
              <div className="text-[9px] truncate">
                {userRole === 'student' && className_ && (
                  <p className="font-medium text-gray-700 truncate">{className_}</p>
                )}
                {userRole === 'student' && teacherName && (
                  <p className="text-gray-600 truncate">{teacherName}</p>
                )}
                {userRole === 'teacher' && className_ && (
                  <p className="text-gray-600 truncate">{className_}</p>
                )}
                {userRole === 'parent' && teacherName && (
                  <p className="text-gray-600 truncate">{teacherName}</p>
                )}
              </div>
              
              {(startTime || endTime) && (
                <div className="text-[9px] text-gray-500 font-medium truncate">
                  {startTime} - {endTime}
                </div>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs z-[100]">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  };

  if (viewMode === 'daily') {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    const todayEntries = entries.filter(entry => entry.dayOfWeek === today);
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Today's Schedule</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onViewModeChange('weekly')}>
              <Calendar className="w-4 h-4 mr-2" />
              Weekly View
            </Button>
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            {onPrint && (
              <Button variant="outline" size="sm" onClick={onPrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {todayEntries.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No classes scheduled for today
            </div>
          ) : (
            todayEntries
              .sort((a, b) => a.startTime.localeCompare(b.startTime))
              .map((entry, index) => (
                <TimetableCard key={index} entry={entry} />
              ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Weekly Timetable</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onViewModeChange('daily')}>
            <Calendar className="w-4 h-4 mr-2" />
            Daily View
          </Button>
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {onPrint && (
            <Button variant="outline" size="sm" onClick={onPrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="min-w-full">
          {/* Header */}
          <div className="grid grid-cols-8 gap-1 mb-1 bg-gray-50 border-b-2 border-gray-200 sticky top-0 z-10">
            <div className="p-2 text-xs sm:text-sm font-semibold text-gray-700 border-r border-gray-200">Time</div>
            {days.map(day => (
              <div key={day} className="p-2 text-xs sm:text-sm font-semibold text-gray-700 text-center border-r border-gray-200 last:border-r-0">
                {day.slice(0, 3)}
              </div>
            ))}
          </div>

          {/* Time slots with entries */}
          <div className="relative">
            {timeSlots.map((time, timeIndex) => {
              return (
                <div key={time} className="grid grid-cols-8 gap-1 border-b border-gray-100">
                  {/* Time column */}
                  <div className="p-1.5 text-[10px] sm:text-xs text-gray-500 font-medium border-r border-gray-200 flex items-start justify-end min-h-[80px] sm:min-h-[100px]">
                    {time}
                  </div>
                  
                  {/* Day columns */}
                  {days.map((day) => {
                    // Find entry that starts at this time for this day
                    const entriesForSlot = groupedEntries[day]?.[time] || [];
                    const entry = entriesForSlot.find(e => {
                      const entryStart = formatTime(e.startTime);
                      return entryStart === time;
                    });

                    // Calculate how many rows this entry spans
                    let rowSpan = 1;
                    if (entry) {
                      const startSlot = timeSlots.findIndex(slot => 
                        timeSlotContainsEntry(slot, entry.startTime, entry.endTime)
                      );
                      const endSlot = timeSlots.findLastIndex(slot => 
                        timeSlotContainsEntry(slot, entry.startTime, entry.endTime)
                      );
                      rowSpan = Math.max(1, endSlot - startSlot + 1);
                    }

                    return (
                      <div 
                        key={`${day}-${time}`}
                        className="border-r border-gray-200 last:border-r-0 p-0.5 sm:p-1 min-h-[80px] sm:min-h-[100px] relative overflow-hidden"
                      >
                        {entry && (
                          <div 
                            className="absolute inset-0.5 sm:inset-1"
                            style={{ 
                              height: rowSpan > 1 ? `calc(${rowSpan} * (min(80px, 100px) + 1px) - 0.5rem)` : 'calc(100% - 0.5rem)',
                              zIndex: 1,
                              maxHeight: 'calc(100% - 0.5rem)'
                            }}
                          >
                            <TimetableCard entry={entry} className="h-full w-full" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableGrid;
